import { LatestQueueItem, OptJobType, useLatestQueueItem, useOptJobMutation } from 'api/optJobs/optJobs.api'
import { useSystem } from 'api/systems/systems.api'
import { useHasPermission } from 'helpers/global.helper/global.helper'
import { getLanguage } from 'localization/localization.helper'
import moment from 'moment'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import authStore from 'store/auth/auth'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import ConfirmDialog from 'ui/components/ConfirmDialog/ConfirmDialog'
import NoPermissionErrorMessage from 'ui/components/ObjectPropertyFormSelector/components/ObjectPropertySettingForm/components/AddDialogActions/NoPermissionErrorMessage/NoPermissionErrorMessage'
import { OptimizeButton } from 'ui/components/OptimizeFormProvider/components/OptimizeButton/OptimizeButton'
import OptimizeWithSubtypes from 'ui/components/OptimizeWithSubTypes/OptimizeWithSubTypes'
import Tooltip from 'ui/components/Tooltip/Tooltip'
import { useSnapshot } from 'valtio'
import { useOptModelOptimizationContext } from '../OptModelOptimizationContext'
import { lastCompletedAt, optimizationIsRunning } from './StartOptimizationButton.helper'

type StartOptimizationButtonProps = {
  optJobType: OptJobType
  permission?: UserPermission
}

/**
 * What the optimize button knows about the last finished optimization.
 *
 * One value for a human, one for a machine, and they answer different questions.
 * `title` is relative localized prose about the most recently settled queue item —
 * a fan-out subtype finishing is worth showing. `lastCompletedAt` is a raw
 * timestamp that may only appear once the whole job finished, because an external
 * suite compares it against the moment it triggered a run and a partially settled
 * job is not a completion. Mid-fan-out the two intentionally disagree.
 */
type OptimizationTooltip = {
  /** Localized prose for the hover tooltip. Empty until the relative time is computed. */
  title: string
  /** `updated_at` of the last fully completed optimization, verbatim ISO 8601, or null. */
  lastCompletedAt: string | null
}

/**
 * Exported for tests: the pairing of prose and raw timestamp is logic, not
 * rendered output, so it is verified with `renderHook` rather than through the
 * component's mocked Tooltip boundary.
 */
export function useTooltip({
  latestQueueItems,
  showSystemSubtypes,
  systemSubtypes,
}: {
  latestQueueItems: LatestQueueItem[]
  showSystemSubtypes: boolean
  systemSubtypes: Subtype[]
}): OptimizationTooltip {
  const { t } = useTranslation()
  const language = getLanguage()
  const [updatedAtFromNow, setUpdatedAtFromNow] = useState('')

  // Find the most recently updated finished queue item (only 'Finished' status, not 'Failed' or 'Unsolvable')
  const latestFinishedItem = useMemo(() => {
    const finishedItems = latestQueueItems.filter((item) => item.queue_item_status === 'Finished')
    if (finishedItems.length === 0) return null

    return finishedItems.reduce((latest, current) => {
      return new Date(current.updated_at) > new Date(latest.updated_at) ? current : latest
    })
  }, [latestQueueItems])

  // Get subtype display names from all finished queue items (only 'Finished' status)
  // Map the internal subtype name to the display name using systemSubtypes
  const subtypeNames = useMemo(() => {
    const finishedItems = latestQueueItems.filter((item) => item.queue_item_status === 'Finished')
    return finishedItems
      .map((item) => {
        const subtypeConfig = systemSubtypes.find((s) => s.subtype === item.subtype)
        return subtypeConfig?.subtype_display_name ?? item.subtype
      })
      .filter((subtype): subtype is string => !!subtype)
  }, [latestQueueItems, systemSubtypes])

  // Format subtype names similar to OptimizeUpdateContent logic
  const formattedSubtypeName = useMemo(() => {
    if (subtypeNames.length === 0) return ''
    if (subtypeNames.length === 1) return subtypeNames[0]
    if (subtypeNames.length === 2) return subtypeNames.join(' & ')
    return `${subtypeNames.slice(0, -1).join(', ')} & ${subtypeNames[subtypeNames.length - 1]}`
  }, [subtypeNames])

  // Update the "from now" time and set up interval to refresh it
  useEffect(() => {
    if (latestFinishedItem?.updated_at) {
      const time = moment(latestFinishedItem.updated_at).locale(moment.locale(language)).fromNow()
      setUpdatedAtFromNow(time)

      const timeInterval = setInterval(() => {
        setUpdatedAtFromNow(moment(latestFinishedItem.updated_at).locale(moment.locale(language)).fromNow())
      }, 60000)

      return () => clearInterval(timeInterval)
    } else {
      setUpdatedAtFromNow('')
    }
  }, [language, latestFinishedItem?.updated_at])

  // Not derived from latestFinishedItem: that is per queue item and settles early
  // in a fan-out. The raw timestamp also does not wait for the effect above to
  // compute the relative prose, so it appears on the first render that has one.
  const completedAt = lastCompletedAt(latestQueueItems)

  // Build tooltip text
  if (updatedAtFromNow === '') return { title: '', lastCompletedAt: completedAt }

  if (showSystemSubtypes && formattedSubtypeName) {
    return {
      title: t('Last updated for {{subtype}} {{updatedAtFromNow}}', {
        subtype: formattedSubtypeName,
        updatedAtFromNow: updatedAtFromNow,
      }),
      lastCompletedAt: completedAt,
    }
  }

  return {
    title: t('Last updated {{updatedAtFromNow}}', { updatedAtFromNow: updatedAtFromNow }),
    lastCompletedAt: completedAt,
  }
}

export default function StartOptimizationButton({
  optJobType,
  permission = 'run_regular_opt',
}: StartOptimizationButtonProps): ReactElement {
  const { t } = useTranslation()
  const { activeSystem, hasAccess } = useAuth()
  const { data: system } = useSystem({ id: activeSystem?.id })
  const [creatingOptJob, setCreatingOptJob] = useState(false)
  const hasPermission = useHasPermission(permission)
  const [isInitSetup, setIsInitSetup] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const { isSuperUser } = useSnapshot(authStore)

  const { data: latestQueueItems = [], status: fetchingLatestQueueItemStatus } = useLatestQueueItem({
    systemId: activeSystem?.id,
    type: optJobType,
  })

  const { mutate: optJobCreate } = useOptJobMutation()

  // Remove this when all OptModels are migrated to DigitalTwin.
  const { unitSettings, isValid } = useOptModelOptimizationContext()
  const hasOptModel = !!system?.primary_opt_model?.id
  const hasDigitalTwin = !!system?.primary_digital_twin?.uid

  const showSystemSubtypes = useMemo(() => {
    if (!hasAccess({ submodule: 'optimize_subnet', module: 'optimize' })) {
      return false
    }

    return (system?.subtypes?.[optJobType]?.length ?? 0) > 0
  }, [system?.subtypes, hasAccess, optJobType])

  const systemSubtypes = useMemo(() => system?.subtypes?.[optJobType] ?? [], [system?.subtypes, optJobType])

  const isJobTypeManuallyDisabled = useMemo(() => {
    const allSubtypes = system?.subtypes?.[optJobType] ?? []
    if (allSubtypes.length === 0) return false
    return allSubtypes.every((s) => s.enable_manually_trigger_opt_job === false)
  }, [system?.subtypes, optJobType])

  const { title: tooltipTitle, lastCompletedAt: completedAt } = useTooltip({
    latestQueueItems,
    showSystemSubtypes,
    systemSubtypes,
  })

  const isOptimizationRunning = optimizationIsRunning(latestQueueItems)

  const subtypes = useMemo(() => {
    return latestQueueItems.map((item) => item.subtype).filter((subtype): subtype is string => !!subtype)
  }, [latestQueueItems])

  // Fuel plan uses different time range: start of day to 30 days later minus 1 hour
  // Other opt job types use start of current hour with no end time
  const isFuelPlan = optJobType === 'fuel_plan'
  const getStartTime = () => (isFuelPlan ? moment().startOf('day') : moment().startOf('hour'))
  const getEndTime = () => (isFuelPlan ? moment().startOf('day').add(30, 'day').subtract(1, 'hour') : undefined)

  const handleOptimizeClick = (): void => {
    if (isSuperUser) {
      setConfirmOpen(true)
    } else {
      handleOptimize()
    }
  }

  const handleOptimize = (): void => {
    if (!activeSystem) return

    setCreatingOptJob(true)

    // Get fresh timestamps on each optimization to avoid stale values if page is kept open
    const startTime = getStartTime()
    const endTime = getEndTime()

    // Build input data based on system type:
    // - Opt model systems: include unit_settings from accumulator levels
    // - Digital twin systems: don't include unit_settings (they don't use editable accumulator levels)
    const hasUnitSettings = hasOptModel && !hasDigitalTwin && Object.keys(unitSettings).length > 0

    optJobCreate(
      {
        opt_job_type: optJobType,
        input_data: {
          opt_start_time: startTime.toISOString(),
          ...(endTime && { opt_end_time: endTime.toISOString() }),
          ...(hasUnitSettings && { unit_settings: unitSettings }),
        },
      },
      {
        onSettled: () => {
          setCreatingOptJob(false)
        },
      }
    )
  }

  useEffect(() => {
    setIsInitSetup(true)
  }, [])

  return (
    <div className="flex flex-col items-end">
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false)
          handleOptimize()
        }}
        textObject={{ title: t('Start optimization'), text: t('Are you sure you want to start an optimization?') }}
      />
      <Tooltip title={tooltipTitle} arrow placement="left" display="block">
        <div className="self-end">
          {showSystemSubtypes ? (
            <OptimizeWithSubtypes
              isValid={isValid}
              disabled={!hasPermission || isOptimizationRunning || isJobTypeManuallyDisabled}
              type={optJobType}
              optStartTime={getStartTime()}
              optEndTime={getEndTime()}
              optimizeAllSubtypes={handleOptimizeClick}
              creatingOptJob={creatingOptJob || fetchingLatestQueueItemStatus === 'pending'}
            />
          ) : (
            <OptimizeButton
              type={optJobType}
              disabled={!hasPermission || isOptimizationRunning || isJobTypeManuallyDisabled}
              onClick={handleOptimizeClick}
              isValid={isValid}
              style={{ minWidth: '130px' }}
              creatingOptJob={creatingOptJob || fetchingLatestQueueItemStatus === 'pending'}
              subtypes={subtypes}
            />
          )}
        </div>
      </Tooltip>

      {/*
        e2e: machine-readable completion time for aurora-e2e — do not remove.
        The tooltip above renders a nearby instant as localized relative prose,
        which cannot be compared against the moment a run was triggered. Absent
        until a whole optimization has completed, so its presence and its value
        together are a real completion signal.
      */}
      {completedAt && <time data-testid="optimize-last-finished" dateTime={completedAt} className="hidden" />}

      {!hasPermission && !!activeSystem && isInitSetup && (
        <NoPermissionErrorMessage
          title={t(`You do not have permission to optimize`)}
          message={t('Unfortunately, you do not have permission to start an optimization on this user/facility')}
        />
      )}
    </div>
  )
}
